import { Component } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { StorageKeyNamesCommon } from 'src/app/constants';
import { StorageService } from 'src/app/shared/services/storage.service';
import { SharedService } from 'src/app/shared/shared.service';
import { AppNameTextFilterComponent } from '../../custom-filters/apps/app-name-text-filter/app-name-text-filter.component';
import { ServerSideGridBase } from '../server-side-grid.base';

/**
 * Common server-side, paginated apps grid (modelled on the audit-logs grid).
 *
 * Reuse it across screens by passing `tableConfigOptions.columns` from the
 * parent to show/hide, toggle sorting, and toggle filtering per column per
 * screen, e.g.:
 *
 *   tableConfigOptions = {
 *     domLayoutType: 'normal',
 *     columns: [
 *       { headerName: 'App Name', hide: false, sortable: true, filter: 'appNameTextFilter' },
 *       { headerName: 'Description', hide: true, sortable: false, filter: false },
 *     ],
 *   };
 *
 * Columns are matched by `headerName`; any column not listed keeps its default
 * visibility. `hide` is always applied; `sortable` and `filter` are applied
 * only when provided (so a column's own default is kept otherwise) — pass
 * `filter: false` to disable a filter, or a filter component name to set one.
 * App Name carries a debounced text filter (like the audit-logs User ID filter).
 */
@Component({
  selector: 'app-apps-grid',
  templateUrl: './apps-grid.component.html',
  styleUrls: ['./apps-grid.component.scss'],
})
export class AppsGridComponent extends ServerSideGridBase {

  protected storageKey = StorageKeyNamesCommon.appsList;
  protected gridIdSuffix = 'appsGrid';

  protected initialFilters = {
    'app-name': '',
  };

  protected initialSorts = {
    app_name: '',
    short_description: '',
  };

  columnDefs: ColDef[] = [
    {
      headerName: '#',
      valueGetter: params => {
        return Number(`${params.node?.rowIndex}`) + 1
      },
      rowDrag: true
    },
    {
      headerName: 'App Name',
      field: 'app_name',
      filter: 'appNameTextFilter',
      minWidth: 200,
    },
    {
      headerName: 'Description',
      field: 'short_description',
      sortable: false,
      minWidth: 240,
    },
  ];

  frameworkComponents = {
    appNameTextFilter: AppNameTextFilterComponent,
  };

  // Field that holds the rank used for promotional ordering.
  protected promoRankField = 'promo_rank';

  constructor(storageService: StorageService, sharedService: SharedService) {
    super(storageService, sharedService);
  }

  /**
   * Unmanaged row drag (managed dragging is unsupported while pagination is on).
   * On drop we move the dragged row to its new position ourselves and give it a
   * promo_rank that sits between its new neighbours' ranks, so its order matches
   * what the user sees without disturbing any other row's rank:
   *   - dropped at the top (no row above)    -> midpoint of 0 and the next rank
   *   - dropped at the bottom (no row below) -> previous rank + 5
   *   - dropped in the middle                -> midpoint of the surrounding ranks
   * The single changed row { app_id, promo_rank } is emitted upward so the parent
   * can persist it immediately via its own API call (no Save button).
   */
  onRowDragEnd(event: any): void {
    const movingData = event.node && event.node.data;
    const overNode = event.overNode;
    if (!movingData || !overNode || overNode.data === movingData) {
      return;
    }

    const rows: any[] = [];
    this.gridApi.forEachNode(node => {
      if (node.data) {
        rows.push(node.data);
      }
    });

    const fromIndex = rows.indexOf(movingData);
    const toIndex = rows.indexOf(overNode.data);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
      return;
    }

    // Move the dragged row to its new slot.
    rows.splice(toIndex, 0, rows.splice(fromIndex, 1)[0]);

    // Pick a promo_rank between the dragged row's new neighbours.
    const newIndex = rows.indexOf(movingData);
    const prev = rows[newIndex - 1];
    const next = rows[newIndex + 1];

    let newRank: number;
    if (!prev) {
      // First row: a value between 0 and the next row's rank (e.g. next 10 -> 5).
      newRank = Number(next[this.promoRankField]) / 2;
    } else if (!next) {
      // Last row: bump just past the previous row's rank.
      newRank = Number(prev[this.promoRankField]) + 5;
    } else {
      // Middle: midpoint between the surrounding ranks.
      newRank = (Number(prev[this.promoRankField]) + Number(next[this.promoRankField])) / 2;
    }

    movingData[this.promoRankField] = newRank;

    // Apply the new order to the grid (unmanaged drag => we update the data).
    this.gridApi.setRowData(rows);

    this.rowActionEvent.emit({
      action: 'promoRankReorder',
      changed: { data: movingData, promo_rank: newRank },
    });
  }

  protected mapFilterModel(filterModel: any): void {
    this.urlParams.filters['app-name'] = filterModel.app_name ? filterModel.app_name.value : '';
  }

  protected mapSortModel(sortModel: any[]): void {
    Object.keys(this.urlParams.sorts).forEach(k => (this.urlParams.sorts[k] = ''));
    sortModel.forEach((elem: any) => {
      if (elem.colId in this.urlParams.sorts) {
        this.urlParams.sorts[elem.colId] = elem.sort;
      }
    });
  }
}
